import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Avatar from "../../VibehutUI/Avatar";
import { Link } from "react-router-dom";
import PasswordIcon from "../../VibehutUI/Icons/PasswordIcon";
import Polygon from "../../VibehutUI/Icons/Polygon";
import Subscriptions from "../../VibehutUI/Icons/Subscriptions";
import { StoreContext } from "../../Context/StoreContext";
import { checkMyRoomSubscription } from "../../utils/utils";
import { getUserData } from "../../services/userService";
import moment from "moment-timezone";
import Button from "../../VibehutUI/Button";
import {
  cancelRequestEvent,
  createEventFromRequestEvent,
} from "../../services/requestEventService";
import Loader from "../../VibehutUI/Loader";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { showLoginModal } from "../../actions/uiModalActions";
import globals from "../../globals";
import RoomModel from "../../VibehutUI/RoomModal";

const getRoute = (event) => {
  if (event.isSubscriber) {
    return `/chat?roomId=${event.room._id}`;
  } else if (event.room.slug) {
    return `/${event.room.slug}`;
  } else {
    return `/rooms/${event.room._id}`;
  }
};

const formatDateTime = (dateTime) => {
  const now = moment();
  const targetDateTime = moment(dateTime);

  const diffInMinutes = targetDateTime.diff(now, "minutes");
  const diffInHours = targetDateTime.diff(now, "hours");
  const diffInDays = targetDateTime.diff(now, "days");
  const zone = moment.tz.guess();
  const abbr = moment.tz(zone).format("z");
  if (diffInDays === 0) {
    if (diffInHours < 1) {
      return `In ${diffInMinutes} minutes · ${targetDateTime.format(
        "h:mm A z",
      )} ${abbr}`;
    }
    if (diffInHours === 0) {
      return `Now · ${targetDateTime.format("h:mm A z")} ${abbr}`;
    }
    if (diffInHours === 1) {
      return `In ${diffInHours} hour · ${targetDateTime.format(
        "h:mm A z",
      )} ${abbr}`;
    }
    return `In ${diffInHours} hours · ${targetDateTime.format(
      "h:mm A z",
    )} ${abbr}`;
  } else if (diffInDays === 1) {
    return `Tomorrow at ${targetDateTime.format("h:mm A z")} ${abbr}`;
  } else {
    return `In ${diffInDays} days · ${targetDateTime.format(
      "D MMMM h:mm A z",
    )} ${abbr}`;
  }
};

function RequestEventCard({ event, setEvents, events }) {
  const { room } = event;
  const { state, dispatch } = useContext(StoreContext);
  const {
    auth: { user },
    subscription: { mySubscriptions },
  } = state;
  const [isUserSubscribed, setIsUserSubscribed] = useState(false);
  const [isLoadingId, setIsLoadingId] = useState(null);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [isOpenPassword, setIsOpenPassword] = useState(false);
  const [roomPassword, setRoomPassword] = useState("");
  const history = useHistory();
  useEffect(() => {
    const mySubscription = checkMyRoomSubscription(
      mySubscriptions,
      event?.room?._id,
    );
    if (mySubscription) {
      setIsUserSubscribed(true);
    }
  }, [mySubscriptions, event?.room?._id]);

  React.useEffect(() => {
    if (isPasswordVerified) {
      onRequestSession();
    }
  }, [isPasswordVerified]);

  const onRequestSession = async () => {
    try {
      const matchedRequestedEvent = event;
      if (!user?.token) {
        return showLoginModal(dispatch);
      }
      const zone = moment.tz.guess();
      if (matchedRequestedEvent) {
        setIsLoadingId(matchedRequestedEvent._id);
        const requestedBy = matchedRequestedEvent.requestedBy.map((u) => u._id);
        const data = {
          startDate: matchedRequestedEvent.startDate,
          endDate: matchedRequestedEvent.endDate,
          room: matchedRequestedEvent.room?._id,
          requestedEventId: matchedRequestedEvent._id,
          requestedBy: [user?._id, ...requestedBy],
          timezone: zone,
        };
        const response = await createEventFromRequestEvent(data, user.token);
        if (response.data.requestEvent) {
          let p_events = [...events];
          p_events = p_events.filter(
            (e) => e._id !== response.data.requestEvent._id,
          );
          setEvents(p_events);
          const route = getRoute(matchedRequestedEvent);
          history.push(route);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingId(null);
    }
  };

  const checkForSubscribedUser = useCallback(() => {
    return (
      room._id &&
      room.isSubscriberRequireApproval &&
      !room.subscriberApprovedUsers?.includes(user?._id)
    );
  }, [
    room.isSubscriberRequireApproval,
    room.subscriberApprovedUsers,
    room._id,
    user?._id,
  ]);

  const checkForPassword = useCallback(() => {
    return (
      room._id &&
      room?.isPasswordProtected &&
      !isPasswordVerified &&
      room?.owner?._id !== user?._id
    );
  }, [
    room?.isPasswordProtected,
    room?.owner?._id,
    isPasswordVerified,
    room._id,
    user?._id,
  ]);

  const isChatRoomOwner = useMemo(() => {
    return room._id && room?.owner?._id === user?._id;
  }, [room?.owner?._id, room._id, user?._id]);

  const checkForNftAccessable = useCallback(() => {
    return room._id && !room?.isAccessible && room?.isNftCollectionRoom;
  }, [room?.isAccessible, room?.isNftCollectionRoom, room._id]);

  const checkForBlocked = useCallback(() => {
    return room._id && room?.blockedUsers?.includes(user?._id);
  }, [room?.blockedUsers, room._id, user?._id]);

  const checkForIsPaidRoomNotSubscribed = useCallback(() => {
    if (room._id && room?.hasSubscription) {
      if (room?.moderators) {
        for (let i = 0; i < room.moderators.length; i++) {
          if (room.moderators[i].toString() === user?._id.toString())
            return false;
        }
      }
      const mySubscription = checkMyRoomSubscription(
        mySubscriptions,
        room?._id,
      );
      if (!mySubscription) {
        return true;
      }
      return false;
    }

    return false;
  }, [room?._id, room?.hasSubscription, mySubscriptions, room._id]);

  const handleRequestSession = () => {
    if (!user?.token) {
      return showLoginModal(dispatch);
    }
    if (isChatRoomOwner) {
      onRequestSession();
      return;
    }
    if (checkForPassword()) {
      setIsOpenPassword(true);
    } else if (checkForSubscribedUser()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody
          message={"The room host has not approved you yet"}
        />,
      );
      return;
    } else if (checkForNftAccessable()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"Non accessible NFT room."} />,
      );
      return;
    } else if (checkForBlocked()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"You are blocked to access this room"} />,
      );
      return;
    } else if (checkForIsPaidRoomNotSubscribed()) {
      globals.showModal("Oops!", <ValidationModalBody message={"Paid Room"} />);
      return;
    } else {
      onRequestSession();
    }
  };

  const onRequestCancel = async (matchedRequestedEvent) => {
    try {
      if (!user?.token) {
        return showLoginModal(dispatch);
      }
      if (matchedRequestedEvent) {
        setIsLoadingId(matchedRequestedEvent._id);
        const response = await cancelRequestEvent(
          matchedRequestedEvent._id,
          user.token,
        );
        if (!response.data?.event) {
          let p_events = [...events];
          p_events = p_events.filter(
            (e) => e._id !== matchedRequestedEvent._id,
          );
          setEvents(p_events);
        } else {
          let p_events = [...events];
          p_events = p_events.filter(
            (e) => e._id !== matchedRequestedEvent._id,
          );
          setEvents([...p_events, response.data?.event]);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingId(null);
    }
  };

  const getCardAction = () => {
    if (isLoadingId === event._id) {
      return (
        <div>
          <Loader />
        </div>
      );
    }
    if (event?.requestedBy.find((u) => u._id === user?._id)) {
      return (
        <div className="flex gap-1">
          <Button
            size="vibhut-sm"
            variant="vibhut-primary-outline"
            onClick={(e) => {
              e.stopPropagation();
              onRequestCancel(event);
            }}
          >
            I'm Interested
          </Button>
        </div>
      );
    }
    return (
      <div className="flex gap-1">
        <Button
          size="vibhut-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRequestSession();
          }}
        >
          Interested
        </Button>
      </div>
    );
  };
  return (
    <div className="flex flex-row sm:flex-col md:flex-row items-center sm:items-start md:items-center justify-between gap-2.5 bg-frescoWhite p-2.5 rounded-2xl">
      <div className="flex items-center gap-2.5">
        <div className="-space-x-4 flex">
          {event.requestedBy?.map((user) => (
            <div
              className="border-3 rounded-full  border-frescoWhite z-10"
              key={user._id}
            >
              <div className="w-7 h-7 rounded-full">
                <Avatar
                  src={getUserData(user).profile_picture}
                  username={getUserData(user).username}
                  size="xs"
                />
              </div>
            </div>
          ))}
        </div>
        <Link to={getRoute(event)}>
          <div className="flex flex-col  ">
            <span className="text-darkNight font-medium text-sm flex items-center gap-0.5">
              {event.room.isPasswordProtected && <PasswordIcon />}{" "}
              {event.room.isNftCollectionRoom && (
                <Polygon
                  bgColor={event.room.isAccessible ? "#FFCC00" : "#B5B5B5"}
                  isInline={true}
                />
              )}
              {event.room.hasSubscription && (
                <Subscriptions
                  color={isUserSubscribed ? "#34C759" : "#7A7A7A"}
                />
              )}
              {event.room.name}
            </span>
            <span className="text-darkGray text-xs">
              {formatDateTime(event.startDate)}
            </span>
          </div>
        </Link>
      </div>
      {getCardAction()}
      <RoomModel
        isOpen={isOpenPassword}
        setIsOpen={setIsOpenPassword}
        roomId={room._id}
        setIsPasswordVerified={setIsPasswordVerified}
        roomPassword={roomPassword}
        setRoomPassword={setRoomPassword}
        buttonTitle={"Interested"}
      />
    </div>
  );
}

const ValidationModalBody = ({ message }) => {
  return (
    <div className="bg-white text-gray-600 dark:text-gray-300 dark:bg-navyGray p-4 relative">
      <p className="text-lg">{message}</p>
    </div>
  );
};

export default RequestEventCard;
